import { localStorageAdapter } from './storage';
import type { ModalStorage } from './storage';
import type { ModalHandle, ModalResult, OpenModalOptions } from './types';

export interface ModalEntry {
  id: string;
  options: OpenModalOptions;
  /** Live state of the footer `Don't show again` checkbox. */
  dontShowAgainChecked: boolean;
}

type Listener = () => void;

/**
 * Module-level modal stack. It is a tiny event emitter (no external state library), so
 * `openModal(...)` works from anywhere — a mounted `ModalProvider` subscribes via
 * `useSyncExternalStore` and renders the stack.
 */
let stack: ModalEntry[] = [];
const listeners = new Set<Listener>();
const resolvers = new Map<string, (result: ModalResult) => void>();
let storage: ModalStorage = localStorageAdapter;
let counter = 0;

function emit(): void {
  for (const listener of listeners) listener();
}

function open(options: OpenModalOptions): ModalHandle {
  let resolveClosed!: (result: ModalResult) => void;
  const closed = new Promise<ModalResult>((resolve) => {
    resolveClosed = resolve;
  });

  // Already suppressed → resolve immediately, never mount.
  if (options.suppressKey != null && storage.get(options.suppressKey)) {
    resolveClosed({ action: null, dontShowAgain: true, suppressed: true });
    return { id: '', close: () => undefined, update: () => undefined, closed };
  }

  counter += 1;
  const id = `modal-${String(counter)}`;
  resolvers.set(id, resolveClosed);
  stack = [...stack, { id, options, dontShowAgainChecked: false }];
  emit();

  return {
    id,
    close: (result) => {
      close(id, result);
    },
    update: (patch) => {
      update(id, patch);
    },
    closed,
  };
}

function setDontShowAgain(id: string, checked: boolean): void {
  stack = stack.map((entry) =>
    entry.id === id ? { ...entry, dontShowAgainChecked: checked } : entry,
  );
  emit();
}

/** Merge `patch` over the options of an open entry; unknown (closed) ids are ignored. */
function update(id: string, patch: Partial<OpenModalOptions>): void {
  if (!stack.some((entry) => entry.id === id)) return;
  stack = stack.map((entry) =>
    entry.id === id ? { ...entry, options: { ...entry.options, ...patch } } : entry,
  );
  emit();
}

function close(id: string, partial?: Partial<ModalResult>): void {
  const entry = stack.find((item) => item.id === id);
  if (entry == null) return;

  const dontShowAgain = partial?.dontShowAgain ?? entry.dontShowAgainChecked;
  const result: ModalResult = {
    action: partial?.action ?? null,
    dontShowAgain,
    suppressed: false,
  };
  if (dontShowAgain && entry.options.suppressKey != null) {
    storage.set(entry.options.suppressKey, true);
  }

  stack = stack.filter((item) => item.id !== id);
  emit();

  entry.options.onClose?.();
  const resolve = resolvers.get(id);
  resolvers.delete(id);
  resolve?.(result);
}

function closeTop(): void {
  const top = stack[stack.length - 1];
  if (top != null) close(top.id);
}

function closeAll(): void {
  for (const entry of [...stack].reverse()) close(entry.id);
}

// subscribe / getSnapshot are passed unbound to useSyncExternalStore, so they are arrow
// properties (never rely on `this`).
export const modalStore = {
  subscribe: (listener: Listener): (() => void) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
  getSnapshot: (): ModalEntry[] => stack,
  setStorage: (next: ModalStorage): void => {
    storage = next;
  },
  open,
  setDontShowAgain,
  update,
  close,
  closeTop,
  closeAll,
};
